import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

type ThemeContextType = {
  isDarkMode: boolean;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType>({} as ThemeContextType);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  const [isDarkMode, setIsDarkMode] = useState(
    colorModeFromLocalStorage === 'dark' || (!colorModeFromLocalStorage && isSystemPreferenceDark)
  );

  useEffect(() => {
    window.localStorage.setItem("colorMode", isDarkMode ? 'dark' : 'light');
    
    // Apply theme to body
    document.body.style.backgroundColor = isDarkMode ? '#141414' : '#ffffff';
    document.body.style.color = isDarkMode ? '#ffffff' : '#000000';

    // Apply theme to Ant Design components
    const root = document.documentElement;
    if (isDarkMode) {
      // Layout
      root.style.setProperty('--ant-layout-bg', '#141414');
      root.style.setProperty('--ant-layout-header-bg', '#1f1f1f');
      root.style.setProperty('--ant-layout-sider-bg', '#1f1f1f');
      root.style.setProperty('--ant-layout-trigger-bg', '#1f1f1f');
      
      // Text and Background
      root.style.setProperty('--ant-bg-color', '#141414');
      root.style.setProperty('--ant-text-color', '#ffffff');
      root.style.setProperty('--ant-text-color-secondary', 'rgba(255, 255, 255, 0.65)');
      root.style.setProperty('--ant-heading-color', 'rgba(255, 255, 255, 0.85)');
      
      // Components
      root.style.setProperty('--ant-component-bg', '#1f1f1f');
      root.style.setProperty('--ant-border-color', '#303030');
      root.style.setProperty('--ant-border-color-split', '#303030');
      
      // Descriptions
      root.style.setProperty('--ant-descriptions-bg', '#141414');
      root.style.setProperty('--ant-descriptions-title-color', '#ffffff');
      root.style.setProperty('--ant-descriptions-item-label-bg', '#1f1f1f');
      root.style.setProperty('--ant-descriptions-item-content-bg', '#141414');
      root.style.setProperty('--ant-descriptions-item-label-color', '#ffffff');
      root.style.setProperty('--ant-descriptions-item-content-color', '#ffffff');
      
      // Menu
      root.style.setProperty('--ant-menu-bg', '#1f1f1f');
      root.style.setProperty('--ant-menu-item-color', '#ffffff');
      root.style.setProperty('--ant-menu-item-active-bg', '#1890ff');
      root.style.setProperty('--ant-menu-item-selected-bg', '#1890ff');
      root.style.setProperty('--ant-menu-sub-bg', '#1f1f1f');
      
      // Table
      root.style.setProperty('--ant-table-bg', '#141414');
      root.style.setProperty('--ant-table-header-bg', '#1f1f1f');
      root.style.setProperty('--ant-table-row-hover-bg', '#1f1f1f');
      root.style.setProperty('--ant-table-header-color', '#ffffff');
      root.style.setProperty('--ant-table-color', '#ffffff');
      
      // Card
      root.style.setProperty('--ant-card-bg', '#1f1f1f');
      root.style.setProperty('--ant-card-head-color', '#ffffff');
      
      // Input
      root.style.setProperty('--ant-input-bg', '#141414');
      root.style.setProperty('--ant-input-border-color', '#303030');
      root.style.setProperty('--ant-input-color', '#ffffff');
      root.style.setProperty('--ant-input-placeholder-color', 'rgba(255, 255, 255, 0.45)');
      
      // Select
      root.style.setProperty('--ant-select-bg', '#141414');
      root.style.setProperty('--ant-select-border-color', '#303030');
      root.style.setProperty('--ant-select-dropdown-bg', '#1f1f1f');
      root.style.setProperty('--ant-select-item-selected-bg', '#1890ff');
      
      // Button
      root.style.setProperty('--ant-btn-default-bg', '#1f1f1f');
      root.style.setProperty('--ant-btn-default-border-color', '#303030');
      root.style.setProperty('--ant-btn-text-color', '#ffffff');
      
      // Modal
      root.style.setProperty('--ant-modal-bg', '#1f1f1f');
      root.style.setProperty('--ant-modal-heading-color', '#ffffff');
      
      // Drawer
      root.style.setProperty('--ant-drawer-bg', '#1f1f1f');
      
      // Tooltip
      root.style.setProperty('--ant-tooltip-bg', '#1f1f1f');

      // Painéis e status (spec 036) — os blocos de resumo da Recarga de Planos usavam cor
      // literal e ficavam ilegíveis no tema escuro. Os pares abaixo são a rampa dark do antd,
      // escolhidos por contraste >= 4.5:1 sobre #1f1f1f.
      root.style.setProperty('--ant-panel-bg', '#1f1f1f');
      root.style.setProperty('--ant-panel-border', '#303030');
      root.style.setProperty('--ant-status-error-fg', '#ff7875');
      root.style.setProperty('--ant-status-error-bg', '#2a1215');
      root.style.setProperty('--ant-status-warning-fg', '#ffc53d');
      root.style.setProperty('--ant-status-warning-bg', '#2b2111');
      root.style.setProperty('--ant-status-success-fg', '#95de64');
      root.style.setProperty('--ant-status-success-bg', '#162312');
      root.style.setProperty('--ant-status-info-bg', '#111d2c');
      root.style.setProperty('--ant-status-info-fg', '#1890ff');
      // `.dark-mode a` já consumia esta var sem que ninguém a declarasse: o link ficava sem cor
      // de link no tema escuro (achado do QA). 6,54:1 sobre #1f1f1f.
      root.style.setProperty('--ant-link-color', '#40a9ff');
      root.style.setProperty(
        '--ant-mono-font',
        "'SFMono-Regular', Menlo, Consolas, monospace"
      );

      // Add dark mode class to body
      document.body.classList.add('dark-mode');
    } else {
      // Reset all variables to light theme
      root.style.setProperty('--ant-layout-bg', '#f0f2f5');
      root.style.setProperty('--ant-layout-header-bg', '#ffffff');
      root.style.setProperty('--ant-layout-sider-bg', '#ffffff');
      root.style.setProperty('--ant-layout-trigger-bg', '#ffffff');
      
      root.style.setProperty('--ant-bg-color', '#ffffff');
      root.style.setProperty('--ant-text-color', 'rgba(0, 0, 0, 0.65)');
      root.style.setProperty('--ant-text-color-secondary', 'rgba(0, 0, 0, 0.45)');
      root.style.setProperty('--ant-heading-color', 'rgba(0, 0, 0, 0.85)');
      
      root.style.setProperty('--ant-component-bg', '#ffffff');
      root.style.setProperty('--ant-border-color', '#d9d9d9');
      root.style.setProperty('--ant-border-color-split', '#d9d9d9');
      
      root.style.setProperty('--ant-menu-bg', '#ffffff');
      root.style.setProperty('--ant-menu-item-color', 'rgba(0, 0, 0, 0.65)');
      root.style.setProperty('--ant-menu-item-active-bg', '#e6f7ff');
      root.style.setProperty('--ant-menu-item-selected-bg', '#e6f7ff');
      root.style.setProperty('--ant-menu-sub-bg', '#ffffff');
      
      root.style.setProperty('--ant-table-bg', '#ffffff');
      root.style.setProperty('--ant-table-header-bg', '#fafafa');
      root.style.setProperty('--ant-table-row-hover-bg', '#fafafa');
      root.style.setProperty('--ant-table-header-color', 'rgba(0, 0, 0, 0.85)');
      root.style.setProperty('--ant-table-color', 'rgba(0, 0, 0, 0.65)');
      
      root.style.setProperty('--ant-card-bg', '#ffffff');
      root.style.setProperty('--ant-card-head-color', 'rgba(0, 0, 0, 0.85)');
      
      root.style.setProperty('--ant-input-bg', '#ffffff');
      root.style.setProperty('--ant-input-border-color', '#d9d9d9');
      root.style.setProperty('--ant-input-color', 'rgba(0, 0, 0, 0.65)');
      root.style.setProperty('--ant-input-placeholder-color', 'rgba(0, 0, 0, 0.45)');
      
      root.style.setProperty('--ant-select-bg', '#ffffff');
      root.style.setProperty('--ant-select-border-color', '#d9d9d9');
      root.style.setProperty('--ant-select-dropdown-bg', '#ffffff');
      root.style.setProperty('--ant-select-item-selected-bg', '#e6f7ff');
      
      root.style.setProperty('--ant-btn-default-bg', '#ffffff');
      root.style.setProperty('--ant-btn-default-border-color', '#d9d9d9');
      root.style.setProperty('--ant-btn-text-color', 'rgba(0, 0, 0, 0.65)');
      
      root.style.setProperty('--ant-modal-bg', '#ffffff');
      root.style.setProperty('--ant-modal-heading-color', 'rgba(0, 0, 0, 0.85)');
      
      root.style.setProperty('--ant-drawer-bg', '#ffffff');
      
      root.style.setProperty('--ant-tooltip-bg', '#ffffff');
      
      // Descriptions
      root.style.setProperty('--ant-descriptions-bg', '#ffffff');
      root.style.setProperty('--ant-descriptions-title-color', 'rgba(0, 0, 0, 0.85)');
      root.style.setProperty('--ant-descriptions-item-label-bg', '#fafafa');
      root.style.setProperty('--ant-descriptions-item-content-bg', '#ffffff');
      root.style.setProperty('--ant-descriptions-item-label-color', 'rgba(0, 0, 0, 0.85)');
      root.style.setProperty('--ant-descriptions-item-content-color', 'rgba(0, 0, 0, 0.85)');

      // Painéis e status (spec 036) — os valores claros são exatamente os que a tela de
      // Recarga de Planos já usava em literal; nenhuma cor nova entra no produto.
      root.style.setProperty('--ant-panel-bg', '#fafafa');
      root.style.setProperty('--ant-panel-border', '#f0f0f0');
      root.style.setProperty('--ant-status-error-fg', '#cf1322');
      root.style.setProperty('--ant-status-error-bg', '#fff1f0');
      // #8c5000 e não o #ad6800 herdado da spec 033: aquele dava 4,25:1 sobre o próprio fundo
      // e 4,41:1 sobre card branco, abaixo dos 4,5:1 de texto normal (R04 do review).
      root.style.setProperty('--ant-status-warning-fg', '#8c5000');
      root.style.setProperty('--ant-status-warning-bg', '#fffbe6');
      root.style.setProperty('--ant-status-success-fg', '#237804');
      root.style.setProperty('--ant-status-success-bg', '#f6ffed');
      root.style.setProperty('--ant-status-info-bg', '#e6f7ff');
      root.style.setProperty('--ant-status-info-fg', '#1669b6');
      root.style.setProperty('--ant-link-color', '#0054b8');
      root.style.setProperty(
        '--ant-mono-font',
        "'SFMono-Regular', Menlo, Consolas, monospace"
      );

      // Remove dark mode class from body
      document.body.classList.remove('dark-mode');
    }

    // Global Styles
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .ant-descriptions-item-label {
        background-color: var(--ant-descriptions-item-label-bg) !important;
        color: var(--ant-descriptions-item-label-color) !important;
      }
      .ant-descriptions-item-content {
        background-color: var(--ant-descriptions-item-content-bg) !important;
        color: var(--ant-descriptions-item-content-color) !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const contextValue = useMemo(
    () => ({
      isDarkMode,
      toggleTheme,
    }),
    [isDarkMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext); 